import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  checkClientInfo,
  checkClientStatusByAbbreviation,
  checkForms,
  getSimpleClientByAbbreviation,
} from "../services/clientService";
import {
  alertOfInactiveClient,
  checkEmailExists,
  createCandidate,
} from "../services/candidateService";
import { createOrderCandidate } from "../services/orderService";
import { Candidate } from "../interfaces/candidate";
import { CandidateCreateOrder } from "../interfaces/order";
import { isValidEmail } from "../services/helpers";

const CandidateRegister = () => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [candidateOrderRegister, setCandidateOrderRegister] =
    useState<CandidateCreateOrder>();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string>();

  const handleInputChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
  ) => {
    setCandidateOrderRegister((prevCandidateOrderRegister) => ({
      ...prevCandidateOrderRegister!,
      [field]: e.target.value,
    }));

    if (field === "abbreviation" && e.target.value.length > 1) {
      const client = await getSimpleClientByAbbreviation(e.target.value);
      if (client != null) {
        setClientName(client.diocese);
      } else {
        setClientName("");
      }
    }

    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    if (!candidateOrderRegister?.candidateEmail)
      errors.candidateEmail = "Email is required.";
    else if (!isValidEmail(candidateOrderRegister.candidateEmail))
      errors.candidateEmail = "Please enter a valid email address.";
    if (!candidateOrderRegister?.candidateFirstName)
      errors.candidateFirstName = "First name is required.";
    if (!candidateOrderRegister?.candidateLastName)
      errors.candidateLastName = "Last name is required.";

    // Email check
    if (
      candidateOrderRegister?.candidateEmail &&
      (await checkEmailExists(candidateOrderRegister.candidateEmail))
    ) {
      errors.candidateEmail = "Email already in use.";
    }

    if (!candidateOrderRegister?.abbreviation)
      errors.abbreviation = "Client Number/Abbreviation is required.";

    if (!candidateOrderRegister?.positionCode) {
      errors.positionCode = "Position Code is required.";
    } else if (
      /[a-zA-Z]/.test(candidateOrderRegister.positionCode.toString())
    ) {
      errors.positionCode = "Position Code must contain numbers only.";
    } else if (
      candidateOrderRegister?.abbreviation &&
      candidateOrderRegister.positionCode
    ) {
      const client = await getSimpleClientByAbbreviation(
        candidateOrderRegister.abbreviation,
      );

      if (client == null) {
        errors.abbreviation = "Abbreviation not found.";
      } else {
        // check client has status of 2 or 3 and is active and questionnaires have been reviewed
        const clientStatus = await checkClientStatusByAbbreviation(
          candidateOrderRegister?.abbreviation,
        );

        // check if client has necessary forms unloaded
        const [formsHere] = await checkForms(client.clientID);
        const exists: boolean = await checkClientInfo(
          candidateOrderRegister.abbreviation,
          candidateOrderRegister.positionCode,
        );

        if (!clientStatus) {
          errors.abbreviation = "Client is not active.";
          await alertInactiveClient(true);
          setErrorMessage(
            "We’ve moved to a new system. The client and Oxford have been notified to activate their account. Once setup is complete, we’ll notify you so you can try again.",
          );
        } else if (!formsHere) {
          errors.abbreviation = "Client is not active.";
          await alertInactiveClient(true);
          setErrorMessage(
            "We’ve moved to a new system. The client and Oxford have been notified to activate their account. Once setup is complete, we’ll notify you so you can try again.",
          );
        } else if (!exists) {
          //fires if ref service but not questionnaire review
          await alertInactiveClient(false);
          errors.abbreviation = "Client information combination not found.";
          errors.positionCode = "   ";
        } else {
          delete errors.abbreviation;
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if ((await validateFields()) === false) return;

      if (candidateOrderRegister) {
        const candidate: Candidate = await createCandidate(
          candidateOrderRegister.candidateEmail,
        );
        candidateOrderRegister.candidateID = candidate.candidateID;
        await createOrderCandidate(candidateOrderRegister);
        handleNavigateToLogin();
      } else {
        setErrorMessage("Registration failed. Please contact support.");
      }
    } catch (error) {
      setErrorMessage("Registration failed. Please contact support.");
      console.error("Error creating order:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToLogin = () => {
    const email = candidateOrderRegister?.candidateEmail;
    localStorage.setItem("email", email || "");
    localStorage.setItem("portalType", "candidate");

    navigate("/login", {
      state: {
        successMessage:
          "You have successfully created an account. Please login to continue!",
      },
    });
  };

  const alertInactiveClient = async (sendToClient: boolean) => {
    try {
      setLoading(true);
      if (!candidateOrderRegister) return;
      await alertOfInactiveClient(candidateOrderRegister, sendToClient);
    } catch (error) {
      console.error("Error notifying client and admin:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-container">
      <div className="text-container">
        <div style={{ display: "flex" }}>
          <div style={{ width: "100%" }}>
            <h1>New Candidate Registration</h1>
          </div>
          <div className="logo-help-wrapper">
            <button
              className="help-icon-button"
              onClick={() => navigate("/help")}
              aria-label="Help"
            >
              <i className="bi bi-question-circle"></i>
            </button>
          </div>
        </div>
        <div className="divider"></div>
        <div className="text-body">
          <b>
            Welcome to Oxford Document Management's Candidate Registration
            Process
          </b>
        </div>
        <br></br>
        <div className="text-body">
          The initial process should take only a few minutes to complete.
          Afterward, you will be redirected to log in at our website to complete
          the necessary requirements for the services our client has selected
          for your background check.
        </div>
        <br></br>
        <div className="text-body">
          Our client should have provided you with their <b>Abbreviation</b> or
          <b> Client Number</b> along with a <b>Position Code</b>. Please enter
          this information below, along with your name and email, to get
          started.
        </div>
        <br></br>
        <div className="text-body">
          If you have any questions, feel free to contact us for assistance.
        </div>
        <br></br>
        <div className="data-container">
          <div
            className="text-sub-sub-header"
            style={{ padding: "0px", flex: "0 0 100%" }}
          >
            Your Information:
          </div>
          <div className="field-stacked" style={{ flex: "0 0 30%" }}>
            <label className="field-label">First Name:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.candidateFirstName ? "is-invalid" : ""
                }`}
                type="text"
                value={candidateOrderRegister?.candidateFirstName || ""}
                onChange={(e) => handleInputChange(e, "candidateFirstName")}
              />
              {validationErrors.candidateFirstName && (
                <div className="invalid-feedback">
                  {validationErrors.candidateFirstName}
                </div>
              )}
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 30%" }}>
            <label className="field-label">Last Name:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.candidateLastName ? "is-invalid" : ""
                }`}
                type="text"
                value={candidateOrderRegister?.candidateLastName || ""}
                onChange={(e) => handleInputChange(e, "candidateLastName")}
              />
              {validationErrors.candidateLastName && (
                <div className="invalid-feedback">
                  {validationErrors.candidateLastName}
                </div>
              )}
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 50%" }}>
            <label className="field-label">Email:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.candidateEmail ? "is-invalid" : ""
                }`}
                type="text"
                value={candidateOrderRegister?.candidateEmail || ""}
                onChange={(e) => handleInputChange(e, "candidateEmail")}
              />
              {validationErrors.candidateEmail && (
                <div className="invalid-feedback">
                  {validationErrors.candidateEmail}
                </div>
              )}
            </div>
          </div>
          <div style={{ flex: "0 0 100%" }}></div>
          <div
            className="text-sub-sub-header"
            style={{ padding: "0px", flex: "0 0 100%" }}
          >
            Client Information:
          </div>
          <div className="field-stacked" style={{ flex: "0 0 30%" }}>
            <label className="field-label">Client Number/Abbreviation:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.abbreviation ? "is-invalid" : ""
                }`}
                type="text"
                value={candidateOrderRegister?.abbreviation || ""}
                onChange={(e) => handleInputChange(e, "abbreviation")}
              />
              {validationErrors.abbreviation && (
                <div className="invalid-feedback">
                  {validationErrors.abbreviation}
                </div>
              )}
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 30%" }}>
            <label className="field-label">Position Code:</label>
            <div className="field-input">
              <input
                className={`form-control ${
                  validationErrors.positionCode ? "is-invalid" : ""
                }`}
                type="text"
                value={candidateOrderRegister?.positionCode || ""}
                onChange={(e) => handleInputChange(e, "positionCode")}
              />
              {validationErrors.positionCode && (
                <div className="invalid-feedback">
                  {validationErrors.positionCode}
                </div>
              )}
            </div>
          </div>
          <div style={{ flex: "0 0 100%" }}>
            Associated Client: <b>{clientName !== "" ? clientName : "None"}</b>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <div>
            <button className="oxbtn btn-purple" onClick={() => handleSave()}>
              Register and Start
            </button>
          </div>
          <div>
            <b>
              If you are a candidate who has previously used the Oxford portal,
              please log in to your existing account{" "}
              <a
                href="https://www.oxfordportal.com/?portal=candidate"
                rel="noopener noreferrer"
              >
                here
              </a>
              .
            </b>
          </div>
        </div>
      </div>
      {loading && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
      {errorMessage && (
        <div className="alert-message-register alert alert-danger alert-dismissible fade show">
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default CandidateRegister;
